import React, { useCallback, useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';

import { StyledTextButton, StyledPopup } from './styles';

import Button from '../../Button';
import Bubble from '../../Bubble';

import { Download } from '@icons/editor';
import { Attach } from '@icons/editor';
import { Close } from '@icons/common';
import { ESC } from '../../../constants/keycodes';

const DocumentToolBar = React.memo(({ onAttach, attachmentsByOrderId }) => {
  const [isOpen, setIsOpen] = useState(false);

  const toggleIsOpen = useCallback(() => {
    setIsOpen(prevIsOpen => !prevIsOpen);
  });

  const handleClick = event => {
    event.stopPropagation();
    event.preventDefault();

    toggleIsOpen();
  };

  const handleKeyDown = event => {
    if (isOpen && event.keyCode === ESC) {
      toggleIsOpen();
    }
  };

  const handleAttach = attachment => {
    onAttach(attachment);
  };

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown, false);

    return () => {
      document.removeEventListener('keydown', handleKeyDown, false);
    };
  }, [isOpen]);

  return (
    <>
      <Bubble
        text={!attachmentsByOrderId.length && 'В заказах нет документов'}
        type="warning"
      >
        <StyledButton
          type="button"
          onClick={handleClick}
          disabled={!attachmentsByOrderId.length}
        >
          Документы
        </StyledButton>
      </Bubble>
      <StyledPopup isOpen={isOpen}>
        {isOpen && (
          <StyledWrap>
            <StyledCloseButton functional onClick={toggleIsOpen}>
              <Close width={16} height={16} />
            </StyledCloseButton>
            <StyledLinks>
              {attachmentsByOrderId.map(
                ({ id: orderId, active, attachments }) => (
                  <StyledLinkWrapper open={active}>
                    <StyledLinkTitle functional data-active={active}>
                      Заказ №{orderId}
                    </StyledLinkTitle>
                    {!!attachments.length ? (
                      attachments.map(({ id, name, url, type }) => (
                        <StyledLink>
                          <StyledDownloadLink
                            href={url}
                            target="_blank"
                            download
                          >
                            <Download width={26} height={26} />
                          </StyledDownloadLink>
                          <StyledAttachButton
                            functional
                            onClick={() =>
                              handleAttach({
                                id,
                                name,
                                type,
                                url,
                              })
                            }
                          >
                            <Attach width={24} height={24} />
                          </StyledAttachButton>
                          <StyledLinkLabel>{name}</StyledLinkLabel>
                        </StyledLink>
                      ))
                    ) : (
                      <StyledLink>В заказе нет документов</StyledLink>
                    )}
                  </StyledLinkWrapper>
                )
              )}
            </StyledLinks>
          </StyledWrap>
        )}
      </StyledPopup>
    </>
  );
});

const StyledButton = styled(StyledTextButton)`
  ${({ disabled }) =>
    disabled &&
    `
      opacity: .5;
      cursor: default;
      user-select: none;
    `}
`;

const StyledLinkTitle = styled.summary`
  line-height: 30px;
  border-top: 1px solid rgb(233, 233, 233);
  font-weight: bold;
  background: whitesmoke;
  text-align: center;
  cursor: pointer;

  &[data-active='false'] {
    opacity: 0.4;
  }
`;

const StyledLinks = styled.div`
  background: #ffffff;
  border-radius: 6px;
  max-height: 380px;
  width: 430px;
  overflow: auto;
`;

const StyledWrap = styled.div`
  position: relative;
`;

const StyledLinkWrapper = styled.details`
  display: flex;
  flex-direction: column;
`;

const StyledLink = styled.div`
  display: flex;
  padding: 10px;
  gap: 10px;
  color: #292929;
  text-decoration: none;
  border-top: 1px solid #e9e9e9;

  &:first-child {
    border-top: none;
  }
`;

const StyledDownloadLink = styled.a`
  &:hover {
    ${Download} path {
      stroke: #007aff;
    }
  }
`;

const StyledAttachButton = styled(Button)`
  ${({ disabled }) =>
    disabled
      ? `
    opacity: 0.2;
  `
      : `
    &:hover {
      cursor: pointer;
      
      path {
        stroke: #007aff;
      }
    }
  `}
`;

const StyledCloseButton = styled(Button)`
  background-color: white;
  position: absolute;
  top: -15px;
  right: -15px;
  border-radius: 100%;
  padding: 6px;
  border: 1px solid #919499;
  line-height: 0;
  width: 30px;
  height: 30px;
  cursor: pointer;
`;

const StyledLinkLabel = styled.div`
  font-size: 15px;
  line-height: 26px;
  white-space: nowrap;
  overflow: hidden;
  width: 100%;
  text-overflow: ellipsis;
`;

DocumentToolBar.propTypes = {
  typeLink: PropTypes.string,
};

DocumentToolBar.defaultProps = {
  links: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      name: PropTypes.string,
      size: PropTypes.string,
      url: PropTypes.string,
    })
  ),
};

export default DocumentToolBar;
